import React, { ReactNode, useEffect, useRef } from 'react';
import { X } from 'lucide-react';
import { TaskTypeModule } from '../../taskTypes/module';

interface TaskExampleModalProps {
  module: TaskTypeModule<any, any>;
  onClose: () => void;
  children: ReactNode;
}

export function TaskExampleModal({ module, onClose, children }: TaskExampleModalProps) {
  const closeRef = useRef<HTMLButtonElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    closeRef.current?.focus();
    return () => {
      previousFocusRef.current?.focus();
      previousFocusRef.current = null;
    };
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="task-example-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex max-h-[90vh] w-full max-w-6xl flex-col rounded-lg bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-4 border-b border-gray-100 px-5 py-4">
          <div className="min-w-0">
            <h2 id="task-example-title" className="text-lg font-semibold text-gray-900">
              Task example · {module.displayName}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              Read-only preview using sample data for this task type.
            </p>
          </div>
          <button
            type="button"
            ref={closeRef}
            aria-label="Close"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-scroll bg-gray-50 px-5 py-5">
          {children}
        </div>
      </div>
    </div>
  );
}
